import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiGet, apiPost } from '../lib/api-client';
import { can as userCan, type BackofficeUser } from '../lib/permissions';

export interface LedgerTick { at: number; resources: string[] }

interface AuthState {
  user: BackofficeUser | null;
  ready: boolean;
  collaborateOpen: boolean;
  ledger: LedgerTick | null;
  can: (page: string, action?: string) => boolean;
  refresh: () => Promise<BackofficeUser | null>;
  login: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  openCollaborate: () => void;
  closeCollaborate: () => void;
  notifyLedger: (resources: string[]) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<BackofficeUser | null>(null);
  const [ready, setReady] = useState(false);
  const [collaborateOpen, setCollaborateOpen] = useState(false);
  const [ledger, setLedger] = useState<LedgerTick | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await apiGet<{ user: BackofficeUser }>('/api/me');
      setUser(data.user);
      return data.user;
    } catch {
      setUser(null);
      return null;
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const login = useCallback(async (email: string) => {
    await apiPost('/api/azure-auth/mock-login', { email });
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await apiPost('/api/azure-auth/logout', {}); } finally { setUser(null); }
  }, []);

  const value = useMemo<AuthState>(() => ({
    user, ready, collaborateOpen, ledger,
    can: (page, action = 'read') => userCan(user, page, action),
    refresh, login, logout,
    openCollaborate: () => setCollaborateOpen(true),
    closeCollaborate: () => setCollaborateOpen(false),
    notifyLedger: (resources) => { if (resources.length) setLedger({ at: Date.now(), resources }); },
  }), [user, ready, collaborateOpen, ledger, refresh, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth requires AuthProvider');
  return ctx;
}
